/**
 * Field Timeline — story-driven view.
 *
 * Track 2: rebuilt from disconnected rows into grouped operational
 * stories. Each story shows "First → Next → After that → Current" and
 * its still-needs-attention next step. Construction language only.
 *
 * Falls back to a flat newest-first list when no story has been built
 * yet (e.g. brand-new sheet) so the page is never empty.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Camera, Mic, Send, Loader2, MessageSquareWarning,
  ShieldAlert, CheckCircle2, ChevronRight, ListChecks, Users,
} from "lucide-react";
import { api } from "@/lib/api";
import FieldRecordsPanel from "@/components/FieldRecordsPanel";


const KIND_META = {
  photo:              { icon: Camera, label: "Photo",            colour: "#0ea5e9" },
  voice_note:         { icon: Mic, label: "Voice note",          colour: "#10b981" },
  revision_send:      { icon: Send, label: "Sent to foreman",    colour: "#6366f1" },
  foreman_ack:        { icon: CheckCircle2, label: "Acknowledged", colour: "#16a34a" },
  foreman_complete:   { icon: ListChecks, label: "Completed",    colour: "#475569" },
  clarification_open: { icon: MessageSquareWarning, label: "Clarification", colour: "#eab308" },
  field_conflict:     { icon: ShieldAlert, label: "Field conflict", colour: "#dc2626" },
};

const STATUS_TONE = {
  conflict_open:             "#dc2626",
  waiting_for_clarification: "#eab308",
  waiting_for_field:         "#f59e0b",
  needs_attention:           "#f97316",
  waiting_for_review:        "#6366f1",
  in_progress:               "#0ea5e9",
  resolved:                  "#16a34a",
  closed:                    "#475569",
};


export default function FieldTimeline() {
  const { sheetId } = useParams();
  const navigate = useNavigate();
  const [stories, setStories] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [filter, setFilter] = useState("all");

  const reload = useCallback(() => {
    api.get(`/sheets/${sheetId}/stories`)
      .then((r) => setStories(r.data?.items || []))
      .catch(() => setStories([]));
    api.get(`/sheets/${sheetId}/field-timeline`)
      .then((r) => setTimeline(r.data))
      .catch(() => setTimeline(null));
  }, [sheetId]);
  useEffect(() => { if (sheetId) reload(); }, [sheetId, reload]);

  const visibleStories = useMemo(() => {
    if (!stories) return [];
    if (filter === "all") return stories;
    return stories.filter((s) => s.status === filter);
  }, [stories, filter]);

  if (stories === null || timeline === null) {
    return (
      <div className="p-8 text-sm text-[var(--fop-text-3)] flex items-center gap-2" data-testid="field-timeline-loading">
        <Loader2 className="animate-spin" size={14} /> Loading the field timeline…
      </div>
    );
  }

  const counts = timeline?.counts || {};
  const projectId = timeline?.project_id;

  return (
    <div className="h-screen flex flex-col bg-[var(--fop-bg)]" data-testid="field-timeline">
      <header className="border-b border-[var(--fop-border)] bg-[var(--fop-surface)] px-4 py-2.5 flex items-center gap-3 flex-wrap">
        <button onClick={() => navigate(-1)} aria-label="Back" data-testid="field-timeline-back"
                className="text-[var(--fop-text-3)] hover:text-[var(--fop-text)]"><ArrowLeft size={16} /></button>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--fop-primary)]">Field timeline</div>
          <div className="text-sm font-semibold text-[var(--fop-text)]" data-testid="field-timeline-headline">
            {stories.length} {stories.length === 1 ? "story" : "stories"} ·
            {" "}{counts.photos || 0} photos · {counts.voice_notes || 0} voice notes · {counts.conflicts || 0} conflicts
          </div>
        </div>
        <div className="ml-auto flex items-center gap-1.5 flex-wrap" data-testid="field-timeline-filters">
          {[
            ["all", "All"],
            ["conflict_open", "Conflict Open"],
            ["waiting_for_clarification", "Waiting For Clarification"],
            ["waiting_for_field", "Waiting For Field"],
            ["waiting_for_review", "Waiting For Review"],
            ["needs_attention", "Needs Attention"],
            ["in_progress", "In Progress"],
            ["resolved", "Resolved"],
            ["closed", "Closed"],
          ].map(([id, label]) => {
            const active = filter === id;
            return (
              <button
                key={id} onClick={() => setFilter(id)}
                data-testid={`field-timeline-filter-${id}`}
                className={`px-2 py-1 rounded text-[10px] font-mono uppercase tracking-wider border ${
                  active
                    ? "border-[var(--fop-primary)]/55 bg-[var(--fop-primary-soft)] text-[var(--fop-primary)]"
                    : "border-transparent text-[var(--fop-text-3)] hover:text-[var(--fop-text)] hover:border-[var(--fop-border)]"
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>
      </header>

      <div className="flex-1 min-h-0 grid grid-cols-[1fr_360px]">
        <main className="overflow-y-auto p-4">
          {visibleStories.length === 0 ? (
            <div className="p-10 text-center text-[var(--fop-text-3)]" data-testid="field-timeline-empty">
              Nothing here yet.
            </div>
          ) : (
            <ol className="space-y-3" data-testid="field-timeline-stories">
              {visibleStories.map((s) => <StoryCard key={s.id} story={s} />)}
            </ol>
          )}
        </main>
        <FieldRecordsPanel sheetId={sheetId} projectId={projectId} anchor={null} />
      </div>
    </div>
  );
}


function StoryCard({ story }) {
  const statusColour = STATUS_TONE[story.status] || "#0ea5e9";
  const topKind = (story.members || []).slice().reverse()
    .find((m) => ["field_conflict", "clarification_open", "revision_send"].includes(m.kind))?.kind
    || story.members?.[0]?.kind
    || "photo";
  const meta = KIND_META[topKind] || KIND_META.photo;
  const Icon = meta.icon;

  return (
    <li
      data-testid={`field-timeline-story-${story.id}`}
      className="rounded border border-[var(--fop-border)] bg-[var(--fop-surface)] p-3"
    >
      <div className="flex items-start gap-2 mb-2">
        <div
          className="mt-0.5 inline-flex items-center justify-center rounded-full"
          style={{ width: 24, height: 24, color: meta.colour, border: `2px solid ${meta.colour}66` }}
        >
          <Icon size={12} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-semibold text-[var(--fop-text)] truncate">
            {meta.label} on {story.sheet_number || "this sheet"}
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
            <span
              className="px-1.5 py-0.5 rounded-full text-[10px] font-mono uppercase tracking-[0.18em]"
              style={{ background: "rgba(0,0,0,0.04)", color: statusColour, border: `1px solid ${statusColour}55` }}
              data-testid={`field-timeline-story-${story.id}-status`}
            >
              {story.status_label || story.status || "Needs Attention"}
            </span>
            {(story.people || []).length > 0 && (
              <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">
                <Users size={10} /> {story.people.join(" · ")}
              </span>
            )}
            <span className="font-mono text-[10px] text-[var(--fop-text-3)] ml-auto">
              {story.last_activity_at ? new Date(story.last_activity_at).toLocaleString() : ""}
            </span>
          </div>
        </div>
      </div>

      {/* Chronological story */}
      <ol className="space-y-1 border-l-2 border-[var(--fop-border)] pl-3 mb-2">
        {(story.members || []).map((m, idx) => {
          const km = KIND_META[m.kind] || KIND_META.photo;
          const MIcon = km.icon;
          const stage = idx === 0 ? "First"
                       : idx === (story.members.length - 1) ? "Now"
                       : (idx === 1 ? "Next" : "After that");
          return (
            <li key={`${m.kind}_${m.id}_${idx}`}
                data-testid={`field-timeline-story-${story.id}-step-${idx}`}
                className="grid grid-cols-[12px_1fr] gap-2 items-start">
              <span className="-ml-[19px] mt-0.5 inline-flex items-center justify-center rounded-full"
                    style={{ width: 14, height: 14, background: "var(--fop-surface)", color: km.colour, border: `2px solid ${km.colour}66` }}>
                <MIcon size={8} />
              </span>
              <div className="text-[12px] text-[var(--fop-text)]">
                <span className="font-mono text-[9px] uppercase tracking-wider text-[var(--fop-text-3)] mr-1">{stage}</span>
                <span className="font-semibold text-[var(--fop-text)]">{km.label}</span>
                {m.actor && <span className="text-[var(--fop-text-3)]"> · {m.actor}</span>}
                {m.title && <div className="text-[var(--fop-text-2)] truncate">{m.title}</div>}
              </div>
            </li>
          );
        })}
      </ol>

      {/* Still needs attention */}
      {(story.open_actions || []).length > 0 && (
        <div className="border-t border-[var(--fop-border)] pt-2 mt-2 flex flex-col gap-1">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--fop-text-3)]">Still needs attention</div>
          {story.open_actions.map((a, i) => (
            <div key={`open_${i}`}
                 data-testid={`field-timeline-story-${story.id}-next-${i}`}
                 className="inline-flex items-center gap-1.5 text-[12px] text-[var(--fop-text)]">
              <ChevronRight size={12} className="text-[var(--fop-text-3)]" />
              <span className="truncate">{a.label}</span>
              {a.assignee_name && <span className="text-[var(--fop-text-3)] text-[11px]">· {a.assignee_name}</span>}
            </div>
          ))}
        </div>
      )}
    </li>
  );
}
